import {
	Alert, Box,
	Button, Divider,
	FormControlLabel,
	List,
	ListItemText,
	Stack, Step, StepContent, StepLabel, Stepper,
	Switch,
	Typography,
} from '@mui/material';
import { withSnackbar } from 'notistack';
import { useState } from 'react';
import CustomModal from '../CustomModal';
import FileUploadZone from '../FileUpload/FileUploadZone';
import { importUsersFromFile } from './UserAccountImport';
import React from 'react';

const generateMailTo = (emails, password) => {
	const mailBody = [
		'Guten Tag,\n',
		'wir haben für Sie ein Konto auf unserer DRV Bund Lernwelt (https://lernwelt.drv-bund.de) eingerichtet.',
		'Bitte melden Sie sich dort mit dem folgenden Initialpasswort an:\n',
		password + '\n\n',
		'Nach der erstmaligen Anmeldung werden Sie aufgefordert die Nutzungsbedingungen zu akzeptieren und anschließend ein neues Passwort zu vergeben.',
	].join('\n');
	return 'mailto:?bcc=' + encodeURIComponent(emails.join(',')) + '&subject=' + encodeURIComponent('Ihr Konto für die DRV Bund Lernwelt') + '&body=' + encodeURIComponent(mailBody);
};

function UserImportDialog() {
	const DEFAULT_PASSWORD = '#J9x$B_q';
	const STATE_UPLOAD = 'UPLOAD';
	const STATE_PROCESSING = 'PROCESSING';
	const STATE_ERROR = 'ERROR';
	const STATE_SUCCESS = 'SUCCESS';

	const accept = {
		'application/vnd.ms-excel': ['.xls'],
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
	};
	const invalidFileTypeMessage = 'Es werden nur Excel-Dateien unterstützt';
	const [state, setState] = useState(STATE_UPLOAD);
	const [modalOpen, setModalOpen] = useState(false);
	const [displayResultDetails, setDisplayResultDetails] = useState(false);
	const [activeSuccessStep, setActiveSuccessStep] = useState(1);
	const [fileName, setFileName] = useState('');
	const [error, setError] = useState('');
	const [result, setResult] = useState();
	const createdAccounts = (result || []).filter((item) => item.result === 'created');

	window.openUploadUser = () => setModalOpen(true);

	const hideModal = () => {
		if (state === STATE_PROCESSING || (state === STATE_SUCCESS && activeSuccessStep < 3 && createdAccounts.length > 0)) {
			return;
		}

		setState(STATE_UPLOAD);
		setError('');
		setResult(null);
		setFileName('');
		setModalOpen(false);
	}

	const handleDrop = (acceptedFiles, rejectedFiles) => {
		if (acceptedFiles.length === 0) {
			if (rejectedFiles.length > 0) {
				setError(invalidFileTypeMessage);
				setState(STATE_ERROR);
			}
			return;
		}

		const file = acceptedFiles[0];

		setState(STATE_PROCESSING);
		setFileName(file.name);
		setResult(null);
		setError('');

		if (!modalOpen) {
			hideModal();
			return;
		}

		importUsersFromFile(file, DEFAULT_PASSWORD).then((result) => {
			setResult(result);
			setState(STATE_SUCCESS);
			setActiveSuccessStep(1);
		}).catch((error) => {
			setError(error.message);
			setState(STATE_ERROR);
		});
	}

	const openCreateExternalReferentModal = () => {
		if (window.openCreateExternalReferentAccount) {
			setModalOpen(false);
			window.openCreateExternalReferentAccount();
		}
	}

	const advanceSuccessStep = () => {
		setActiveSuccessStep(activeSuccessStep + 1);
	};

	const getElements = () => {
		switch (state) {
			case STATE_PROCESSING:
				return {
					content: (
						<div style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							padding: '4rem 0'
						}}>
							<Typography sx={{ marginTop: 2 }}
										textAlign={'center'}>{`Datei "${fileName}" wird importiert und Konten werden erstellt`}</Typography>
						</div>
					)
				};

			case STATE_ERROR:
				return {
					content: (<Alert severity={'error'}>{error}</Alert>),
					actions: (<Button onClick={() => setState(STATE_UPLOAD)}>Erneut versuchen</Button>)
				};

			case STATE_SUCCESS:
				const emails = createdAccounts.map((item) => item.email);

				return {
					content: (<>
						{createdAccounts.length === 0 &&
							<Alert severity={'success'}>Es wurden keine neuen Konten erstellt, da alle Teilnehmenden bereits einen Zugang haben.</Alert>}
						{createdAccounts.length > 0 &&
							<Stepper activeStep={activeSuccessStep} orientation="vertical">
								<Step active={activeSuccessStep <= 1}>
									<StepLabel>Upload / Kontoanlage</StepLabel>
									<StepContent>
										<Stack spacing={1}>
											<Alert severity={'success'}>Die Anlage der Konten war erfolgreich!</Alert>
											{createdAccounts.length > 0 && <FormControlLabel
												control={<Switch
													checked={displayResultDetails}
													onChange={() => setDisplayResultDetails(!displayResultDetails)}
												/>} label={'Details anzeigen'}
											/>}
											{displayResultDetails && createdAccounts.length > 0 && <>
												<Typography>Für die folgenden Teilnehmenden wurden Konten angelegt:</Typography>
												<List>
													{createdAccounts.map((item, index) => (<ListItemText
														key={`created-account-${index}`}
														primary={item.email}
													/>))}
												</List>
											</>}
										</Stack>
									</StepContent>
								</Step>
								<Step>
									<StepLabel>Infomail an Teilnehmende versenden</StepLabel>
									<StepContent>
										<Alert severity={'error'}>
											<Typography variant={'body2'}>Die Teilnehmenden benötigen für die Anmeldung noch das Initialpasswort. Nachfolgende Schaltfläche öffnet eine neue, vordefinierte E-Mail in HCL Notes.</Typography>
										</Alert>
										<Box sx={{display: 'flex', justifyContent: 'center', pt: 2}}>
											<Button
												href={generateMailTo(emails, DEFAULT_PASSWORD)}
												variant={activeSuccessStep === 1 ? 'contained' : 'outlined'}
												onClick={advanceSuccessStep}
											>Infomail senden</Button>
										</Box>
									</StepContent>
								</Step>
								<Step>
									<StepLabel>Bestätigung</StepLabel>
									<StepContent>
										<Alert severity={'warning'}>
											<Typography variant={'body2'}>Da vom System nicht erkannt werden kann, ob die E-Mail erfolgreich versandt wurde, ist eine Bestätigung über nachfolgende Schaltfläche notwendig.</Typography>
										</Alert>
										<Box sx={{display: 'flex', justifyContent: 'center', pt: 2}}>
											<Button
												variant={'contained'}
												color={'success'}
												onClick={advanceSuccessStep}
											>Bestätigen</Button>
										</Box>
									</StepContent>
								</Step>
							</Stepper>}
					</>),
					actions: (createdAccounts.length === 0 || activeSuccessStep === 3 ? <>
						<Button onClick={() => setState(STATE_UPLOAD)}>Weitere Konten anlegen</Button>
						<Button variant={'contained'} onClick={hideModal}>Schließen</Button>
					</> : undefined)
				};

			case STATE_UPLOAD:
			default:
				return {
					content: (<>
						<Typography variant={'body1'} sx={{ pb: 1 }}>Konten für externe Teilnehmende
							anlegen</Typography>
						<FileUploadZone onDrop={handleDrop} maxFiles={1} accept={accept}
										invalidFileTypeMessage={invalidFileTypeMessage} />
						<Divider sx={{ my: 3 }}><Typography variant={'body2'}>ODER</Typography></Divider>
						<Button fullWidth variant={'outlined'} onClick={openCreateExternalReferentModal}>
							Konto für externe Referierende anlegen
						</Button>
					</>)
				};
		}
	}
	const elements = getElements();

	return (
		<CustomModal
			closeOnBackdropClick={false}
			open={modalOpen}
			onClose={hideModal}
			title="Konten anlegen"
			maxWidth={'sm'}
			actions={elements.actions || null}
			isProcessing={state === STATE_PROCESSING}
		>
			{elements.content}
		</CustomModal>
	);
}

export default withSnackbar(UserImportDialog);
